// TODO: CHECK
import { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AutoSizer, List, WindowScroller } from 'react-virtualized';

import config from '@/config';
import ProfileDataContext from '@/context/ProfileDataContext';
import useCache from '@/hooks/useCache';
import useStateHandler from '@/hooks/useStateHandler';
import GalleryService from '@/services/gallery.service';
import ForwardArrowIcon from '@/shared-components/icons/components/public/ForwardArrowIcon';
import InfiniteScroll from '@/shared-components/lists/components/InfiniteScroll';
import Loader from '@/shared-components/loader/Loader';
import Text from '@/shared-components/text/components/Text';

import DeleteDraftButton from '../drafts/components/DeleteDraftButton';
import PopUpConfirmDeleteDraft from '../drafts/components/PopUpConfirmDeleteDraft';

import './index.css';

export default function ProfileArtworkDrafts({ variant = 'default' } = {}) {
  const { stateHandler } = useStateHandler();
  const { profileData, setProfileData } = useContext(ProfileDataContext);
  const { loading, fetchData, setFetchData, loadMoreData } = useCache(
    `${profileData._id}_artwork_drafts`,
    `${config.apis.api.url}/drafts`,
    {
      injectToken: true,
      invalidateWhen: ["ARTWORK_DRAFT_SAVED", "NEW_ARTWORK_REQUEST", `${profileData._id}_REFRESH`],
    }
  );
  const navigate = useNavigate();
  const [activeDraftId, setActiveDraftId] = useState(null);
  const userType = (profileData?.user_type || '').toString().toLowerCase();
  const isArtistProfile = userType === 'artist' || userType === 'gallery_artist';

  function removeDraft(draftId) {
    const newTotalDocs = fetchData.totalDocs - 1;
    setFetchData({
      ...fetchData,
      totalDocs: newTotalDocs,
      data: fetchData.data.filter((draft) => draft._id !== draftId),
    });
    setProfileData({
      ...profileData,
      draft_count: newTotalDocs,
    });
    GalleryService.removeArtworkDraft(draftId);
    setActiveDraftId(null);
  }

  const isPopupVariant = variant === 'popup';
  const POPUP_ITEM_GAP = 12;
  const BASE_ROW_HEIGHT = 56;
  const effectiveRowHeight = isPopupVariant ? BASE_ROW_HEIGHT + POPUP_ITEM_GAP : BASE_ROW_HEIGHT;

  return (
    <>
      {loading ? (
        <Loader className="artwork-drafts__loading" thin />
      ) : fetchData.data.length === 0 ? (
        <Text className="artwork-drafts__no-drafts font-size-small" paragraph>
          No artwork drafts available.
        </Text>
      ) : (
        <div className="artwork-drafts__container">
          <InfiniteScroll
            className="post-tags-virtual-list__container remove-scrollbar"
            onLoadMore={loadMoreData}
            shouldLoadMore={fetchData.data.length < fetchData.totalDocs}
            offsetElements={10}
            listLength={fetchData.data.length}
            defaultInfiniteLoaderClassName="post-tags-virtual-list__spinner"
          >
            {({ infiniteScrollAssigner }) => (
              <WindowScroller>
                {({ height, isScrolling, onChildScroll, scrollTop }) => (
                  <AutoSizer disableHeight>
                    {({ width }) => (
                      <List
                        autoHeight
                        width={width === 0 ? 0.1 : width}
                        height={height * fetchData.data.length - 0.001}
                        isScrolling={isScrolling}
                        onScroll={onChildScroll}
                        rowCount={fetchData.data.length}
                        rowHeight={effectiveRowHeight}
                        scrollTop={scrollTop}
                        overscanRowCount={10}
                        rowRenderer={({ index, key, style }) => {
                          const draft = fetchData.data[index];
                          const rowStyle = isPopupVariant
                            ? { ...style, height: effectiveRowHeight }
                            : style;
                          const isLastRow = index === fetchData.data.length - 1;

                          const isSentForApproval = isArtistProfile
                            && (draft?.draft_status || '').toString().toLowerCase() === 'pending';
                          const draftCard = (
                            <div
                              className={`artwork-drafts__draft element-non-selectable${isSentForApproval ? ' artwork-drafts__draft--sent' : ''}`}
                              onClick={
                                isSentForApproval
                                  ? undefined
                                  : () =>
                                    navigate("/profile/add-artwork", {
                                      state: {
                                        draftData: draft,
                                        from: true,
                                      },
                                    })
                              }
                            >
                              {!isSentForApproval && (
                                <DeleteDraftButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveDraftId(draft._id);
                                  }}
                                />
                              )}

                              {!isSentForApproval && activeDraftId === draft._id && (
                                <PopUpConfirmDeleteDraft
                                  open={activeDraftId === draft._id}
                                  close={() => setActiveDraftId(null)}
                                  draftName={
                                    draft.draft_container.artwork_about.artwork_title
                                  }
                                  onConfirm={(e) => {
                                    e.stopPropagation();
                                    removeDraft(draft._id);
                                    stateHandler.set("temporalPopup", {
                                      text: `Draft deleted successfully`,
                                    });
                                  }}
                                />
                              )}

                              <Text className="artwork-drafts__draft-title dots-on-overflow font-size-medium">
                                {draft.draft_container.artwork_about.artwork_title}
                              </Text>
                              {isSentForApproval ? (
                                <Text className="artwork-drafts__draft-status font-size-small">
                                  Sent
                                </Text>
                              ) : (
                                <ForwardArrowIcon className="artwork-drafts__enter-draft" />
                              )}
                            </div>
                          );

                          return (
                            <div key={key} style={rowStyle} ref={infiniteScrollAssigner(index)}>
                              {isPopupVariant ? (
                                <div className="pop-up-drafts__item-wrapper">
                                  {draftCard}
                                  {!isLastRow && <div className="pop-up-drafts__item-spacer" />}
                                </div>
                              ) : (
                                draftCard
                              )}
                            </div>
                          );
                        }}
                      />
                    )}
                  </AutoSizer>
                )}
              </WindowScroller>
            )}
          </InfiniteScroll>
        </div>
      )}
    </>
  );
}
